import { Component } from '@theme/component';

/**
 * HH · Más vendidos por categoría (adaptado de Ola · Más vendidos).
 *
 * Pestañas accesibles (patrón tablist de ARIA):
 * - Toque o clic: muestra esa categoría.
 * - Teclado: flechas izquierda y derecha, Inicio y Fin pasan de pestaña y la muestran.
 * - Las categorías que no se ven llegan dentro de un <template>; se arman la primera vez que se
 *   abren, ya visibles, para que el carrusel y las galerías de las tarjetas se midan bien.
 * - El enlace «Ver todo» del encabezado lleva siempre a la categoría abierta.
 * - En el celular, la fila de pestañas se desliza para dejar a la vista la elegida.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement} [tablist]
 * @property {HTMLAnchorElement} [moreLink]
 * @property {HTMLElement} [moreContext]
 *
 * @extends {Component<Refs>}
 */
export class HhMasVendidos extends Component {
  requiredRefs = ['tabs', 'panels'];

  /**
   * Muestra la categoría de la pestaña tocada.
   * @param {Event} event
   */
  select(event) {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLButtonElement)) return;
    this.#activate(tab);
  }

  /**
   * Flechas, Inicio y Fin dentro de la fila de pestañas.
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    const { tabs } = this.refs;
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';

    let next = current;
    if (event.key === forward) next = (current + 1) % tabs.length;
    else if (event.key === backward) next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    const tab = tabs[next];
    if (!tab) return;
    tab.focus({ preventScroll: true });
    this.#activate(tab);
  }

  /** @param {HTMLButtonElement} tab */
  #activate(tab) {
    const { tabs, panels, moreLink, moreContext } = this.refs;
    const index = tabs.indexOf(tab);
    if (index === -1 || tab.getAttribute('aria-selected') === 'true') {
      this.#reveal(tab);
      return;
    }

    tabs.forEach((item, position) => {
      const selected = position === index;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
    });

    this.setAttribute('data-switched', '');
    panels.forEach((panel, position) => {
      if (position === index) {
        panel.hidden = false;
        this.#build(panel);
      } else {
        panel.hidden = true;
      }
    });

    if (moreLink && tab.dataset.url) moreLink.href = tab.dataset.url;
    if (moreContext && tab.dataset.context) moreContext.textContent = `: ${tab.dataset.context}`;

    this.#reveal(tab);
  }

  /**
   * Arma la categoría la primera vez que se abre.
   * @param {HTMLElement} panel
   */
  #build(panel) {
    const template = panel.querySelector(':scope > template');
    if (!(template instanceof HTMLTemplateElement)) return;
    panel.replaceChildren(document.importNode(template.content, true));
  }

  /**
   * Desliza la fila de pestañas (celular) para que la elegida quede a la vista, sin mover la página.
   * @param {HTMLElement} tab
   */
  #reveal(tab) {
    const { tablist } = this.refs;
    if (!tablist || tablist.scrollWidth <= tablist.clientWidth) return;

    const listBox = tablist.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    const offset = tabBox.left - listBox.left - (listBox.width - tabBox.width) / 2;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    tablist.scrollBy({ left: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('hh-mas-vendidos')) {
  customElements.define('hh-mas-vendidos', HhMasVendidos);
}
